import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Loader2, Save, RotateCcw, Search, ExternalLink, X } from "lucide-react";

const field = "w-full border border-[#E5E5E5] bg-white px-4 py-2.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-restrained-blue";

function Editor({ page, onClose, onSaved }) {
  const base = { title: "", description: "", canonical: "", h1: "", og_image: "", robots: "" };
  const [form, setForm] = useState({ ...base, ...(page.override || {}) });
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/seo", { path: page.path, ...form });
      toast.success("SEO override saved");
      onSaved();
    } catch { toast.error("Save failed"); }
    setSaving(false);
  };

  const reset = async () => {
    setResetting(true);
    try {
      await api.delete(`/admin/seo?path=${encodeURIComponent(page.path)}`);
      toast.success("Reverted to catalog default");
      onSaved();
    } catch { toast.error("Nothing to reset"); }
    setResetting(false);
  };

  const rows = [
    { k: "title", label: "Title tag", ph: page.title },
    { k: "description", label: "Meta description", ph: page.description, textarea: true },
    { k: "h1", label: "H1 heading", ph: page.h1 },
    { k: "canonical", label: "Canonical URL", ph: "Leave blank to use the page's own URL" },
    { k: "og_image", label: "OG / social image URL", ph: "https://…" },
  ];

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="seo-editor">
      <div className="absolute inset-0 bg-deep-navy/40" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white h-full overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-[#E5E5E5] px-6 py-4 flex items-center justify-between">
          <div>
            <p className="overline text-charcoal/50">{page.group}</p>
            <p className="font-mono text-sm text-deep-navy">{page.path}</p>
          </div>
          <button onClick={onClose} data-testid="seo-editor-close" className="text-charcoal/50 hover:text-deep-navy"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-5">
          {rows.map((r) => (
            <div key={r.k}>
              <label className="overline text-charcoal/60 block mb-2">{r.label}</label>
              {r.textarea
                ? <textarea rows={3} className={`${field} resize-none`} value={form[r.k]} onChange={set(r.k)} placeholder={r.ph} data-testid={`seo-field-${r.k}`} />
                : <input className={field} value={form[r.k]} onChange={set(r.k)} placeholder={r.ph} data-testid={`seo-field-${r.k}`} />}
              <p className="text-xs text-charcoal/40 mt-1 truncate">Default: {r.ph || "—"}</p>
            </div>
          ))}
          <div>
            <label className="overline text-charcoal/60 block mb-2">Indexing</label>
            <select className={field} value={form.robots} onChange={set("robots")} data-testid="seo-field-robots">
              <option value="">Index &amp; follow (default)</option>
              <option value="noindex,follow">No-index, follow</option>
              <option value="noindex,nofollow">No-index, no-follow</option>
            </select>
          </div>
        </div>
        <div className="sticky bottom-0 bg-white border-t border-[#E5E5E5] px-6 py-4 flex items-center gap-3">
          <button onClick={save} disabled={saving} data-testid="seo-save" className="inline-flex items-center gap-2 bg-deep-navy text-white px-5 py-2.5 text-sm hover:bg-restrained-blue transition-colors disabled:opacity-60">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save override
          </button>
          {page.override && (
            <button onClick={reset} disabled={resetting} data-testid="seo-reset" className="inline-flex items-center gap-2 border border-[#E5E5E5] px-4 py-2.5 text-sm text-charcoal/70 hover:border-deep-navy transition-colors disabled:opacity-60">
              {resetting ? <Loader2 size={15} className="animate-spin" /> : <RotateCcw size={15} />} Reset
            </button>
          )}
          <a href={page.path} target="_blank" rel="noreferrer" className="ml-auto inline-flex items-center gap-1 text-sm text-restrained-blue hover:underline"><ExternalLink size={14} /> View</a>
        </div>
      </div>
    </div>
  );
}

export default function SeoAdmin() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [group, setGroup] = useState("All");
  const [editing, setEditing] = useState(null);
  const [indexing, setIndexing] = useState(false);

  const runIndex = async () => {
    setIndexing(true);
    try {
      const { data: r } = await api.post("/admin/seo/google-index");
      toast.success(`Sitemap ${r.sitemap} · ${r.indexed} URLs sent to Google` + (r.failed ? ` · ${r.failed} failed` : ""));
    } catch {
      toast.error("Google indexing failed — check the service account key");
    }
    setIndexing(false);
  };

  const load = () => api.get("/admin/seo").then(({ data }) => setData(data)).catch(() => setData({ pages: [], overridden: 0 }));
  useEffect(() => { load(); }, []);

  const groups = useMemo(() => ["All", ...Array.from(new Set((data?.pages || []).map((p) => p.group)))], [data]);
  const filtered = useMemo(() => (data?.pages || []).filter((p) =>
    (group === "All" || p.group === group) &&
    (!q || p.path.toLowerCase().includes(q.toLowerCase()) || (p.title || "").toLowerCase().includes(q.toLowerCase()))
  ), [data, q, group]);

  if (!data) return <div className="py-32 flex justify-center"><Loader2 className="animate-spin text-restrained-blue" /></div>;

  return (
    <div>
      <div className="mb-6">
        <h1 className="font-serif text-4xl text-deep-navy">SEO manager</h1>
        <p className="text-charcoal/60">{data.pages.length} programmatic pages · {data.overridden} with overrides. Blank fields fall back to catalog defaults.</p>
      </div>

      <div className="mb-6 flex flex-wrap items-center justify-between gap-3 border border-[#E5E5E5] bg-off-white px-5 py-4" data-testid="seo-google-panel">
        <div>
          <p className="font-serif text-lg text-deep-navy">Google Search Console</p>
          <p className="text-sm text-charcoal/60">Submit the sitemap and request a recrawl of all programmatic pages via the Indexing API.</p>
        </div>
        <button onClick={runIndex} disabled={indexing} data-testid="seo-google-index" className="inline-flex items-center gap-2 bg-deep-navy text-white px-5 py-2.5 text-sm hover:bg-plum transition-colors disabled:opacity-60">
          {indexing ? <Loader2 size={15} className="animate-spin" /> : <Search size={15} />} {indexing ? "Submitting to Google…" : "Submit sitemap & request indexing"}
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal/40" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search path or title…" data-testid="seo-search" className={`${field} pl-9 w-72`} />
        </div>
        <div className="flex flex-wrap gap-2">
          {groups.map((g) => (
            <button key={g} onClick={() => setGroup(g)} data-testid={`seo-group-${g.toLowerCase().replace(/\s+/g, "-")}`}
              className={`px-3 py-1.5 text-xs border transition-colors ${group === g ? "bg-deep-navy text-white border-deep-navy" : "bg-white text-charcoal/70 border-[#E5E5E5] hover:border-deep-navy"}`}>{g}</button>
          ))}
        </div>
      </div>

      <div className="bg-white border border-[#E5E5E5]" data-testid="seo-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-charcoal/50 border-b border-[#E5E5E5]">
              <th className="px-4 py-3 font-normal overline">Path</th>
              <th className="px-4 py-3 font-normal overline">Title</th>
              <th className="px-4 py-3 font-normal overline">Status</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => (
              <tr key={p.path} className="border-b border-[#F0F0F0] hover:bg-off-white cursor-pointer" data-testid={`seo-row-${p.path}`} onClick={() => setEditing(p)}>
                <td className="px-4 py-3 font-mono text-xs text-deep-navy max-w-[260px] truncate">{p.path}</td>
                <td className="px-4 py-3 text-charcoal/80 max-w-[320px] truncate">{p.override?.title || p.title}</td>
                <td className="px-4 py-3">
                  {p.override
                    ? <span className="inline-block px-2 py-0.5 text-xs" style={{ color: "#5C2D50", backgroundColor: "#5C2D5015" }}>Override</span>
                    : <span className="inline-block px-2 py-0.5 text-xs bg-off-white text-charcoal/50">Default</span>}
                  {p.override?.robots && <span className="ml-2 inline-block px-2 py-0.5 text-xs bg-destructive/10 text-destructive">no-index</span>}
                </td>
                <td className="px-4 py-3 text-right"><span className="text-xs text-restrained-blue">Edit</span></td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={4} className="px-4 py-10 text-center text-charcoal/50">No pages match.</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && <Editor page={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}
